import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';

import Ctas from '../../../Core/Ctas';

const margin = '20px';

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 2rem;

  > li,
  article,
  div {
    width: 100%;
  }

  &.column {
    flex-direction: column;

    > div:nth-child(n),
    article:nth-child(n) {
      max-width: 100%;
    }
  }

  &.center-aligned,
  &.center {
    flex-direction: column;
    text-align: center;

    > div:nth-child(n),
    article:nth-child(n) {
      max-width: 100%;
    }
  }

  &.product-steps {
    margin: 0;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    &.product-steps {
      div.title {
        margin-bottom: 0px;
        h2 {
          font-size: 54px;
          line-height: 1.5;
        }
      }
    }
    &.product-image {
      div.title {
        h2 {
          font-size: 1.5rem;
          line-height: 130%;
        }
      }
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    margin: 0 auto 2rem;

    &.product-image {
      div.title {
        h2 {
          font-size: 2.25rem;
        }
      }
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.xlg')}) {
    max-width: 900px;
    flex-direction: row;
    align-items: center;

    &.product-steps {
      div.title {
        h2 {
          font-size: 72px;
        }
      }
    }

    > div:nth-child(1n),
    article:nth-child(1n) {
      max-width: 400px;
    }

    > div:nth-child(2n),
    article:nth-child(2n) {
      max-width: 820px;
    }

    > div.title {
      max-width: 100% !important;
    }
  }
`;

export const Wrapper = styled.ul`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${margin};

  > li {
    width: 100%;
  }

  li:not(:first-child) {
    margin-top: 0;
  }

  &.product-step-card {
    text-align: center;
    > li {
      margin: 0 auto;
      line-height: 1.5;
    }
  }

  &.blog-card,
  &.blog-card-updated {
    > li {
      display: block;
    }
  }

  &.blog-card-updated {
    gap: 1.5rem;
  }

  &.brand-ambassadors {
    justify-content: center;
    align-items: center;
  }

  &.columns-4 {
    flex-direction: row;
    gap: 0.75rem;
    > li {
      width: calc((100% / 2) - (${margin}) / 2);

      @supports (-ms-ime-align: auto) {
        width: calc((100% / 2) - (${margin}) / 2) - 1px);
      }
    }
  }

  @media only screen and (min-width: 700px) {
    &.blog-card,
    &.blog-card-updated {
      flex-direction: row;
      > li {
        display: block;
        width: calc(100% / 3 - (1.5rem * 2 / 3));

        @supports (-ms-ime-align: auto) {
          /* Fixes Edge */
          width: calc(100% / 3 - (1.5rem * 2 / 3) - 1px);
        }
      }
    }
  }

  /* Fixes IE For blog cards before medium */
  @media only screen and (min-width: 700px) and (-ms-high-contrast: none),
    (-ms-high-contrast: active) {
    &.blog-card,
    &.blog-card-updated {
      > li {
        width: calc(100% / 3 - (1.5rem * 2 / 3) - 1px);
      }
    }
  }

  /* Product Card styling up to medium */
  @media only screen and (max-width: ${themeGet('breakpoints.md')}) {
    &.product-card {
      margin: -60px 0;
      /* 168px * 0.55 bc 168px is the max height of the product card images on mobile, and they are displaced 0.45% of the Y value */
      padding-top: calc(168px * 0.55);
      > li {
        margin: 60px 0;
      }
    }
  }

  /* Feature, Blog, and product card styling after tablet */
  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    flex-direction: row;

    > li {
      /* Width of each card is equal to 100% width / amount of cards per row - (margin * 2) as we want to subtract it equally from both sides */
      width: calc(
        (100% / ${({ columnCount }) => columnCount}) - (${margin} / 2)
      );

      @supports (-ms-ime-align: auto) {
        /* Fixes Edge */
        width: calc(
          (100% / ${({ columnCount }) => columnCount}) - (${margin} * 2) - 1px
        );
      }
    }

    &.columns-4 {
      gap: 1.5rem;
      > li {
        /* Width of each card is equal to 100% width / amount of cards per row - (margin * 2) as we want to subtract it equally from both sides */
        width: calc((100% / 2) - 0.75rem);

        @supports (-ms-ime-align: auto) {
          /* Fixes Edge */
          width: calc((100% / 2) - 0.75rem - 1px);
        }
      }
    }

    &.blog-card,
    &.blog-card-updated {
      > li {
        display: block;
        width: calc(
          (100% / ${({ columnCount }) => columnCount}) - (1.5rem * 2 / 3)
        );

        @supports (-ms-ime-align: auto) {
          /* Fixes Edge */
          width: calc(
            (100% / ${({ columnCount }) => columnCount}) - (1.5rem * 2 / 3) -
              1px
          );
        }
      }
    }

    &.product-card {
      padding-top: 100px;

      &.columns-1 {
        padding-top: 0;
      }
    }

    &.feature-card {
      &.columns-3 {
        > li {
          width: calc((100% / 2) - (${margin} / 2));

          @supports (-ms-ime-align: auto) {
            /* Fixes Edge */
            width: calc((100% / 2) - (${margin} / 2) - 1px);
          }
        }
      }

      &.digital-tools {
        &.columns-3 {
          > li {
            width: calc((100% / 2) - (${margin} * 2));
          }
        }
      }

      &.online-security,
      &.additional-services {
        &.columns-3 {
          > li {
            width: calc(100% - (${margin} * 2));
          }
        }
      }
    }

    &.product-card.columns-2 {
      padding-top: 50px;
    }

    &.brand-ambassadors {
      align-items: stretch;
    }
  }

  /* Fixes IE for feature and blog cards */
  @media only screen and (min-width: ${themeGet(
    'breakpoints.md',
  )}) and (-ms-high-contrast: none),
    (-ms-high-contrast: active) {
    > li {
      width: calc(
        (100% / ${({ columnCount }) => columnCount}) - (${margin} * 2) - 1px
      );
    }

    &.blog-card,
    &.blog-card-updated {
      > li {
        width: calc(
          (100% / ${({ columnCount }) => columnCount}) - (1.5rem * 2) - 1px
        );
      }
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    &.feature-card {
      &.online-security,
      &.additional-services {
        &.columns-3 {
          > li {
            width: calc(
              (100% / ${({ columnCount }) => columnCount}) - (10px * 2)
            );
          }
        }
      }
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.xlg')}) {
    &.columns-4 {
      > li {
        /* Width of each card is equal to 100% width / amount of cards per row - (margin * 2) as we want to subtract it equally from both sides */
        width: calc((100% / ${({ columnCount }) => columnCount}) - 1.125rem);

        @supports (-ms-ime-align: auto) {
          /* Fixes Edge */
          width: calc(
            (100% / ${({ columnCount }) => columnCount}) - 1.125rem - 1px
          );
        }
      }
    }

    &.product-card {
      &.columns-2 {
        padding-top: 0;
      }
    }

    &.feature-card {
      &.columns-3 {
        > li {
          width: calc(
            (100% / ${({ columnCount }) => columnCount}) - (10px * 2)
          );

          @supports (-ms-ime-align: auto) {
            /* Fixes Edge */
            width: calc(
              (100% / ${({ columnCount }) => columnCount}) - (10px * 2) - 1px
            );
          }
        }
      }

      &.online-security,
      &.additional-services {
        &.columns-3 {
          > li {
            width: calc((100% / 3) - (10px * 2) - 1px);
          }
        }
      }

      &.digital-tools {
        &.columns-3 {
          > li {
            width: calc(
              (100% / ${({ columnCount }) => columnCount}) - (10px * 2)
            );
          }
        }
      }
    }
  }

  &.faq-card {
    @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
      justify-content: center;
      > li {
        width: calc((100% / 2) - ${margin});

        @supports (-ms-ime-align: auto) {
          /* Fixes Edge */
          width: calc((100% / 2) - ${margin} - 1px);
        }
      }
    }
    @media only screen and (min-width: ${themeGet('breakpoints.xlg')}) {
      > li {
        width: calc((100% / 3) - ${margin});

        @supports (-ms-ime-align: auto) {
          /* Fixes Edge */
          width: calc((100% / 3) - ${margin} - 1px);
        }
      }
    }
  }

  &.feature-cards {
    @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
      > li {
        width: calc((100% /  ${({ columnCount }) => columnCount}) - ${margin});

        @supports (-ms-ime-align: auto) {
          /* Fixes Edge */
          width: calc((100% /  ${({ columnCount }) => columnCount}) - ${margin} - 1px);
        }
      }
    }
  }

  &.product-image { 
    @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
      display: grid;
      grid-auto-columns: 1fr;
      grid-template-rows: repeat(5, min-content);
      column-gap: 1.5rem;
      row-gap: 0;
      > li {
        width: 100%
      }
    }

    @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
      column-gap: 3rem;
      row-gap: 0;
    }
  }
`;

export const ShowMore = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Body = styled.div`
  margin-bottom: 3rem;

  &.center-aligned,
  &.center {
    text-align: center;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    max-width: 900px;
    margin: 0 auto 3rem;
  }
`;

export const BottomBody = styled.div`
  margin-top: 3rem;
  text-align: center;

  p {
    font-weight: ${themeGet('fontWeights.bold')};
  }
`;

export const StyledCtas = styled(Ctas)`
  align-items: center;
`;
